import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef
} from 'react'
import {
    executionReportsDatabase,
    reportsDatabase,
    documentationDatabase
} from '../indexedDB'
import { useAuth, useConnectionContext, useReportsContext, useUser } from '.'
import {
    executionReportsRoutes,
    pdfMakeRoutes as newPdfMakeRoutes,
    reportsRoutes,
    documentsRoutes
} from '../routes'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    LianearProgresDialog,
    ReportCommitDialog,
    ReportMessagesDialog
} from '../dialogs'
import {
    detectIf3DModelExist,
    /* saveReport, */ translateSubSystem
} from '../config'
import { useLoadingContext } from './Loading.context'
import { BacklogsResume } from '../dialogs/BacklogsResume.dialog'
import { SocketConnection } from '../connections'
import { editReport, getReportByIndex } from '../routes/reports.routes'
import ExecutionReportsService from '../services/execution-reports.service'
import ReportsService from '../services/reports.service'
import { useStatus } from './Status.context'

export const ExecutionReportContext = createContext()

const cleanAzureUrl = (url) => {
    if (!url) return ''
    const separatorIndex = url.indexOf('?')
    return separatorIndex !== -1 ? url.substring(0, separatorIndex) : url
}

const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ab], { type: mimeString })
}

export const processImagesForOffline = (item) => {
    const updatedItem = { ...item }

    if (updatedItem.messages && updatedItem.messages.length > 0) {
        updatedItem.messages = updatedItem.messages.map((msg) => {
            if (
                msg &&
				msg.urlBase64 &&
				(!msg.urlImageMessage ||
					msg.urlImageMessage.length === 0 ||
					!msg.urlImageMessage.startsWith('http'))
            ) {
                try {
                    const blob = dataURItoBlob(msg.urlBase64)
                    const blobUrl = URL.createObjectURL(blob)
                    return {
                        ...msg,
                        urlImageMessage: blobUrl
                    }
                } catch (e) {
                    console.error('Error creating blob for message:', e)
                    return msg
                }
            }
            return msg
        })
    }

    if (updatedItem.backlogList && updatedItem.backlogList.length > 0) {
        updatedItem.backlogList = updatedItem.backlogList.map((backlog) => {
            if (backlog && backlog.images && backlog.images.length > 0) {
                const processedImages = backlog.images.map((img) => {
                    if (
                        img &&
						img.urlBase64 &&
						(!img.url || img.url.length === 0 || !img.url.startsWith('http'))
                    ) {
                        try {
                            const blob = dataURItoBlob(img.urlBase64)
                            const blobUrl = URL.createObjectURL(blob)
                            return {
                                ...img,
                                url: blobUrl
                            }
                        } catch (e) {
                            console.error('Error creating backlog blob:', e)
                            return img
                        }
                    }
                    return img
                })
                return { ...backlog, images: processedImages }
            }
            return backlog
        })
    }

    return updatedItem
}

const hasExecutionReportImages = (execReportData) => {
    if (!execReportData || !execReportData.group) return false

    for (const groupKey in execReportData.group) {
        const items = execReportData.group[groupKey]
        if (Array.isArray(items)) {
            for (const item of items) {
                if (!item) continue

                // Check messages for images
                if (item.messages && Array.isArray(item.messages)) {
                    const hasMsgImg = item.messages.some(
                        (msg) => msg && msg.urlImageMessage
                    )
                    if (hasMsgImg) return true
                }

                // Check backlogList for images
                if (item.backlogList && Array.isArray(item.backlogList)) {
                    for (const backlog of item.backlogList) {
                        if (backlog && backlog.images && Array.isArray(backlog.images)) {
                            const hasBacklogImg = backlog.images.some(
                                (img) => img && img.url
                            )
                            if (hasBacklogImg) return true
                        }
                    }
                }
            }
        }
    }
    return false
}

const injectBase64IntoExecutionReport = (execReportData, bkgImagesMap) => {
    if (!execReportData || !execReportData.group) return execReportData

    const newExecReportData = JSON.parse(JSON.stringify(execReportData))

    for (const groupKey in newExecReportData.group) {
        const items = newExecReportData.group[groupKey]
        if (Array.isArray(items)) {
            newExecReportData.group[groupKey] = items.map((item) => {
                if (!item) return item
                const newItem = { ...item }

                if (newItem.messages && Array.isArray(newItem.messages)) {
                    newItem.messages = newItem.messages.map((msg) => {
                        if (msg && msg.urlImageMessage) {
                            const cleanedUrl = cleanAzureUrl(msg.urlImageMessage)
                            if (bkgImagesMap[cleanedUrl]) {
                                return { ...msg, urlBase64: bkgImagesMap[cleanedUrl] }
                            }
                        }
                        return msg
                    })
                }

                if (newItem.backlogList && Array.isArray(newItem.backlogList)) {
                    newItem.backlogList = newItem.backlogList.map((backlog) => {
                        if (!backlog) return backlog
                        const newBacklog = { ...backlog }
                        if (newBacklog.images && Array.isArray(newBacklog.images)) {
                            newBacklog.images = newBacklog.images.map((img) => {
                                if (img && img.url) {
                                    const cleanedUrl = cleanAzureUrl(img.url)
                                    if (bkgImagesMap[cleanedUrl]) {
                                        return { ...img, urlBase64: bkgImagesMap[cleanedUrl] }
                                    }
                                }
                                return img
                            })
                        }
                        return newBacklog
                    })
                }
                return processImagesForOffline(newItem)
            })
        }
    }
    return newExecReportData
}

export const ExecutionReportProvider = (props) => {
    const { userData } = useAuth()
    const {
        admin,
        isOperator,
        isSapExecutive,
        isShiftManager,
        isChiefMachinery,
        isSuperAdmin,
        site,
        isOperatorInspector,
        isUniversalInspector,
        isUniversalChief,
        isObserver
    } = useUser()
    const { initiatePdfGeneration, setstatus } = useStatus()
    const { setLoadingLogo } = useLoadingContext()
    const { isOnline, wasOffline } = useConnectionContext()
    const {
        saveReportLocal,
        setMessage,
        report,
        setReport,
        getReports,
        otIndex,
        reports,
        setReports,
        elevarReporte,
        updateLocalReport
    } = useReportsContext()
    const [executionReport, setExecutionReport] = useState()
    const [backlogImages, setBacklogImages] = useState({})
    const [loadingImages, setLoadingImages] = useState(false)
    const downloadedImagesIdRef = useRef(new Set())
    const [augmentedExecutionReport, setAugmentedExecutionReport] = useState()
    const [syncQueue, setSyncQueue] = useState([])
    const [processedCount, setProcessedCount] = useState(0)
    const [processedCounts, setProcessedCounts] = useState({})
    const [retryTrigger, setRetryTrigger] = useState(0)
    const currentlyFetchingRef = useRef(null)
    const cachedExecutionsRef = useRef({}) // Cache structure: { [otIndex]: { report, executionReport } }
    const syncRetryTimeoutRef = useRef(null)
    const blobUrlsRef = useRef(new Set())
    const isProcessingRef = useRef(false)

    // Initialize queue from IndexedDB on mount
    useEffect(() => {
        const loadPersistedQueue = async () => {
            await executionReportsDatabase.initDb()

            // Migrate existing localStorage items to IndexedDB
            if (userData?._id) {
                const savedQueue = localStorage.getItem(`syncQueue_${userData._id}`)
                if (savedQueue) {
                    try {
                        const parsed = JSON.parse(savedQueue)
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            for (const item of parsed) {
                                await executionReportsDatabase.guardarItemSync({
                                    id: `${item.type}_${item.reportId}_${item.page || ''}_${item.index || 0}`,
                                    ...item,
                                    status: 'pending',
                                    createdAt: Date.now(),
                                    updatedAt: Date.now()
                                })
                            }
                            localStorage.removeItem(`syncQueue_${userData._id}`)
                        }
                    } catch (e) {
                        console.error('[Sync] Error migrating localStorage queue:', e)
                    }
                }
            }

            const items =
				(await executionReportsDatabase.obtenerItemsSyncPendientes()) || []
            if (items.length > 0) {
                setSyncQueue(items)
            }
        }

        loadPersistedQueue()
    }, [userData?._id])

    // Persist queue to IndexedDB whenever it changes
    useEffect(() => {
        if (userData?._id && syncQueue.length > 0) {
            syncQueue.forEach((item) => {
                try {
                    executionReportsDatabase.guardarItemSync({
                        ...item,
                        id:
							item.id ||
							`${item.type}_${item.reportId}_${item.page || ''}_${item.index || 0}`,
                        status: 'pending',
                        updatedAt: Date.now()
                    })
                } catch (err) {
                    console.error(
                        '[SyncQueue] Failed to persist sync item to IndexedDB:',
                        err
                    )
                }
            })
        }
    }, [syncQueue, userData?._id])

    // Re-hydrate queue and sync loading state
    useEffect(() => {
        if (!executionReport || !executionReport.group || !executionReport._id) {
            return
        }

        const unsavedItems = []
        let needsReportUpdate = false
        const updatedGroup = { ...executionReport.group }

        Object.keys(updatedGroup).forEach((page) => {
            let pageUpdated = false
            const currentItems = updatedGroup[page]
            const newItems = currentItems.map((item, index) => {
                if (!item) return item

                const isInQueue = syncQueue.some(
                    (q) =>
                        q.page === page &&
						q.index === index &&
						q.executionId === executionReport._id
                )

                // Case 1: Item is unsaved but NOT in queue (e.g. after refresh)
                if (item.saved === false && !isInQueue) {
                    unsavedItems.push({
                        item,
                        page,
                        index,
                        executionId: executionReport._id,
                        reportId: report?._id || executionReport.report
                    })
                    pageUpdated = true
                    needsReportUpdate = true
                    return { ...item, loading: true }
                }

                // Case 2: Item IS in queue but loading flag is false (e.g. sync started but UI state reset)
                if (isInQueue && !item.loading) {
                    pageUpdated = true
                    needsReportUpdate = true
                    return { ...item, loading: true }
                }

                return item
            })

            if (pageUpdated) {
                updatedGroup[page] = newItems
            }
        })

        if (unsavedItems.length > 0) {
            console.log(
                `[Sync] Re-hydrating queue with ${unsavedItems.length} local items.`
            )
            setSyncQueue((prev) => {
                const newQueue = [...prev]
                unsavedItems.forEach((item) => {
                    const exists = newQueue.some(
                        (q) =>
                            q.page === item.page &&
							q.index === item.index &&
							q.executionId === item.executionId
                    )
                    if (!exists) newQueue.push(item)
                })
                return newQueue
            })
        }

        if (needsReportUpdate) {
            console.log(
                '[Sync] Updating executionReport to reflect loading state for pending items.'
            )
            setExecutionReport((prev) => {
                if (!prev || prev._id !== executionReport._id) return prev
                return { ...prev, group: updatedGroup }
            })
        }
    }, [syncQueue, executionReport?._id]) // Depend on syncQueue and executionReport._id for re-hydration

    const itemsPendingSync = syncQueue.length

    const uploadProgress = useMemo(() => {
        const total = processedCount + itemsPendingSync
        if (total === 0 || itemsPendingSync === 0) return 100
        return Math.round((processedCount / total) * 100)
    }, [processedCount, itemsPendingSync])

    const isSyncing = itemsPendingSync > 0

    const reportMap = useMemo(() => {
        const map = new Map()
        reports.forEach((r) => map.set(String(r._id), r))
        return map
    }, [reports])

    const globalSyncStatus = useMemo(() => {
        const reportIdsInQueue = [
            ...new Set(syncQueue.map((q) => String(q.reportId)))
        ]
        const allRelevantReportIds = [
            ...new Set([...reportIdsInQueue, ...Object.keys(processedCounts)])
        ]

        return allRelevantReportIds
            .map((reportId) => {
                const pending = syncQueue.filter(
                    (q) => String(q.reportId) === reportId
                ).length
                const processed = processedCounts[reportId] || 0
                const total = pending + processed

                let status = 'pendiente'
                if (pending > 0) {
                    if (
                        syncQueue.length > 0 &&
						String(syncQueue[0].reportId) === reportId
                    ) {
                        status = 'sincronizando'
                    } else {
                        status = 'sincronizando'
                    }
                } else if (processed > 0) {
                    status = 'sincronizado'
                }

                // Try to find the friendly OT ID
                const reportMetadata = reportMap.get(String(reportId))
                const otId =
					reportMetadata?.newIdIndex || reportMetadata?.idIndex || reportId

                return {
                    id: reportId,
                    otId,
                    processed,
                    pending,
                    total,
                    status,
                    progress: `${processed}/${total}`
                }
            })
            .filter((s) => s.total > 0)
            .sort((a, b) => {
                const statusOrder = { sincronizando: 0, pendiente: 1, sincronizado: 2 }
                return statusOrder[a.status] - statusOrder[b.status]
            })
    }, [syncQueue, processedCounts, reportMap])

    const reportSyncStatus = useMemo(() => {
        const status = globalSyncStatus.find(
            (s) => String(s.id) === String(report?._id)
        )
        if (status) {
            return {
                itemsPendingSync: status.pending,
                isSyncing: status.pending > 0,
                uploadProgress:
					status.total === 0
					    ? 100
					    : Math.round((status.processed / status.total) * 100)
            }
        }
        return { itemsPendingSync: 0, isSyncing: false, uploadProgress: 100 }
    }, [globalSyncStatus, report?._id])

    useEffect(() => {
        if (itemsPendingSync === 0) {
            setProcessedCount(0)
            setProcessedCounts({})
        }
    }, [itemsPendingSync])

    /* useEffect(() => {
		const activeReportIds = new Set(syncQueue.map((q) => String(q.reportId)));
		setProcessedCounts((prev) => {
			const next = { ...prev };
			let changed = false;
			Object.keys(next).forEach((id) => {
				if (!activeReportIds.has(id)) {
					delete next[id];
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [syncQueue]); */

    useEffect(() => {
        const runSync = async () => {
            if (isProcessingRef.current || syncQueue.length === 0 || !isOnline) {
                return
            }

            isProcessingRef.current = true
            console.log(
                `[SyncLoop] Processing task: ${syncQueue[0].page}[${syncQueue[0].index}] for OT ${syncQueue[0].reportId}`
            )

            try {
                const task = syncQueue[0]

                if (task.type === 'start') {
                    console.log(
                        `[SyncLoop] Processing START task for OT ${task.reportId}`
                    )
                    await executionReportsRoutes.startExecutionReport(task.reportId, { suppressGlobalErrorHandler: true })

                    const syncItemId =
						task.id ||
						`${task.type}_${task.reportId}_${task.page || ''}_${task.index || 0}`
                    await executionReportsDatabase.eliminarItemSync(syncItemId)

                    setProcessedCounts((prev) => ({
                        ...prev,
                        [task.reportId]: (prev[task.reportId] || 0) + 1
                    }))
                    setProcessedCount((prev) => prev + 1)

                    isProcessingRef.current = false
                    setSyncQueue((prev) => prev.slice(1))

                    return
                }

                if (!task.executionId || !task.reportId) {
                    console.warn('[SyncLoop] Task missing critical IDs, skipping:', task)
                    setSyncQueue((prev) => prev.slice(1))
                    return
                }

                const itemWithImages = await processImagesForItem(
                    task.item,
                    task.page,
                    task.index,
                    { reportId: task.reportId, executionId: task.executionId }
                )
                const { loading, saved, ...itemToSend } = itemWithImages

                const response = await executionReportsRoutes.saveExecutionReportByItem(
                    task.executionId,
                    task.page,
                    task.index,
                    itemToSend,
                    task.reportId,
                    { suppressGlobalErrorHandler: true }
                )

                const serverReport = response.data
                const serverItem = serverReport.group?.[task.page]?.[task.index]

                console.log('[SyncLoop] ✅ Success. Updating UI and DB.')

                // 1. Update the executionReport (Functional Update)
                setExecutionReport((prevReport) => {
                    if (
                        !prevReport ||
						prevReport._id !== task.executionId ||
						!prevReport.group[task.page]
                    ) {
                        return prevReport
                    }

                    const newGroupItems = [...prevReport.group[task.page]]
                    newGroupItems[task.index] = {
                        ...newGroupItems[task.index],
                        ...(serverItem || {}),
                        loading: false,
                        saved: true
                    }

                    const updatedReport = {
                        ...prevReport,
                        group: { ...prevReport.group, [task.page]: newGroupItems }
                    }

                    return updatedReport
                })

                // Update Local DB directly with the new item data for persistence
                await executionReportsDatabase.initDb()
                const currentLocal = await executionReportsDatabase.obtener(
                    task.executionId
                )
                if (currentLocal && currentLocal.group[task.page]) {
                    currentLocal.group[task.page][task.index] = {
                        ...currentLocal.group[task.page][task.index],
                        ...(serverItem || {}),
                        loading: false,
                        saved: true
                    }
                    await executionReportsDatabase.actualizar(currentLocal)
                }

                // 2. Update parent report list
                if (reports && response.data.progress !== undefined) {
                    const newProgress = response.data.progress
                    setReports((prev) =>
                        prev.map((r) =>
                            r._id === task.reportId ? { ...r, progress: newProgress } : r
                        )
                    )

                    const parentReport = reports.find((r) => r._id === task.reportId)
                    if (parentReport) {
                        reportsDatabase.actualizar({
                            ...parentReport,
                            progress: newProgress
                        })
                    }
                }

                setProcessedCounts((prev) => ({
                    ...prev,
                    [task.reportId]: (prev[task.reportId] || 0) + 1
                }))
                setProcessedCount((prev) => prev + 1)
                actualizacionEjecucion(Date.now())

                const syncItemId =
					task.id ||
					`${task.type}_${task.reportId}_${task.page || ''}_${task.index || 0}`
                await executionReportsDatabase.eliminarItemSync(syncItemId)

                isProcessingRef.current = false
                setSyncQueue((prev) => prev.slice(1))
            } catch (error) {
                console.error('[SyncLoop] ❌ Error:', error)
                const status = error.response?.status

                if (status === 400 || status === 404 || status === 401 || status === 403) {
                    console.warn(`[SyncLoop] Permanent or permission error (${status}) for OT ${syncQueue[0]?.reportId}. Removing item from active sync loop.`)
                    const task = syncQueue[0]
                    const syncItemId = task.id || `${task.type}_${task.reportId}_${task.page || ''}_${task.index || 0}`
                    try {
                        await executionReportsDatabase.actualizarItemSync(syncItemId, {
                            status: 'permission_denied',
                            error: error.response?.data?.message || `Error ${status}: Acceso denegado`
                        })
                    } catch (e) {
                        console.error('[SyncLoop] Error marking sync item as failed:', e)
                    }
                    isProcessingRef.current = false
                    setSyncQueue((prev) => prev.slice(1))
                } else {
                    // Network issue, retry after timeout
                    console.log('[SyncLoop] Network issue. Retrying in 10s...')
                    syncRetryTimeoutRef.current = setTimeout(() => {
                        setRetryTrigger((t) => t + 1)
                    }, 10000)
                }
            } finally {
                isProcessingRef.current = false
            }
        }

        runSync()

        return () => {
            if (syncRetryTimeoutRef.current) {
                clearTimeout(syncRetryTimeoutRef.current)
                syncRetryTimeoutRef.current = null
            }
        }
    }, [syncQueue, isOnline, retryTrigger])

    useEffect(() => {
        if (executionReport && Object.keys(backlogImages).length > 0) {
            console.log(
                '[ExecutionContext] Augmenting executionReport with base64 images.'
            )
            const newAugmentedReport = injectBase64IntoExecutionReport(
                executionReport,
                backlogImages
            )
            setAugmentedExecutionReport(newAugmentedReport)
        } else if (executionReport && Object.keys(backlogImages).length === 0) {
            setAugmentedExecutionReport(executionReport)
        } else {
            setAugmentedExecutionReport(null)
        }
    }, [executionReport, backlogImages])

    const [contentData, setContentData] = useState([])
    const [indexGroup, setIndexGroup] = useState()
    const [avanceHoja, setAvanceHoja] = useState(0)
    const [avanceTotal, setAvanceTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [loadingMessage, setLoadingMessage] = useState('')
    const [isDownloadingPautas, setIsDownloadingPautas] = useState(false)
    const [downloadPautasProgress, setDownloadPautasProgress] = useState(0)
    const [downloadPautasStatusText, setDownloadPautasStatusText] = useState('')
    const cancelDownloadRef = useRef(false)
    const hideTimerRef = useRef(null)
    const downloadAbortControllerRef = useRef(null)
    const downloadGenerationRef = useRef(0)

    const cancelDownloadPautas = useCallback(() => {
        cancelDownloadRef.current = true
        if (downloadAbortControllerRef.current) {
            downloadAbortControllerRef.current.abort()
        }
    }, [])
    const [messageOnReport, setMessageOnReport] = useState('')
    const [ejecucionActualizada, actualizacionEjecucion] = useState(0)
    const [messageBacklog, setMessageBacklog] = useState('')
    const [machineData, setMachineData] = useState()
    const [habilite3D, setHabilite3D] = useState(false)
    const [subSistem, setSubSistem] = useState()
    const [puedeEnviar, setPuedeEnviar] = useState(false)
    const [puedeCerrar, setPuedeCerrar] = useState(false)
    const [puedeRechazar, setPuedeRechazar] = useState(false)
    const [puedeTerminarJornada, setPuedeTerminarJornada] = useState(false)
    const [materialesPreview, setMaterialesPreview] = useState()
    const [totalBacklogs, setTotalBacklogs] = useState(0)
    const [isTermJornada, setIsTermJornada] = useState(false)
    const [openReportCommitModal, setOpenReportCommitModal] = useState(false)
    const [messageType, setMessageType] = useState()
    const [openMessagesModal, setOpenMessagesModal] = useState(false)
    const [contenidoResumenBacklog, setContenidoResumenBacklog] = useState()
    const [openResumeBacklogsDialog, setOpenResumeBacklogsDialog] =
		useState(false)
    const [openMenu, setOpenMenu] = useState(false)
    const [toForward, setToForward] = useState(false)
    const [ejecucion, setEjecucion] = useState({
        inicio: null,
        termino: null,
        userId: null
    })
    const [openPreviewModal, setOpenPreviewModal] = useState(false)
    const openBacklogsResume = () => {
        if (executionReport && executionReport.group) {
            const groupedBacklogs = {}
            Object.keys(executionReport.group).forEach((key) => {
                const itemsWithBacklog = executionReport.group[key].filter(
                    (item) => item.backlogList && item.backlogList.length > 0
                )
                if (itemsWithBacklog.length > 0) {
                    groupedBacklogs[key] = itemsWithBacklog
                }
            })
            setContenidoResumenBacklog(groupedBacklogs)
        }
        setOpenResumeBacklogsDialog(true)
        setOpenMenu(false)
    }
    const navigate = useNavigate()
    const location = useLocation()
    useEffect(() => {
        executionReportsDatabase.initDb()
    }, [])

    const processImagesForItem = async (item, page, index, contextIds = {}) => {
        if (!isOnline) return item

        const reportId = contextIds.reportId || report?._id
        const executionId = contextIds.executionId || executionReport?._id

        if (!reportId || !executionId) {
            console.warn('[SyncLoop] processImagesForItem called without report/execution IDs, skipping image upload')
            return item
        }

        const updatedItem = { ...item }

        if (updatedItem.messages && updatedItem.messages.length > 0) {
            const processedMessages = await Promise.all(
                updatedItem.messages.map(async (msg) => {
                    if (
                        msg &&
                        msg.urlBase64 &&
                        (!msg.urlImageMessage ||
                            msg.urlImageMessage.length === 0 ||
                            msg.urlImageMessage.startsWith('blob:') ||
                            !msg.urlImageMessage.startsWith('http'))
                    ) {
                        try {
                            const blob = dataURItoBlob(msg.urlBase64)
                            const formData = new FormData()
                            formData.append('image', blob, `image-${Date.now()}.jpg`)
                            formData.append('reportId', reportId)
                            formData.append('executionId', executionId)
                            formData.append('indexGroup', page)
                            formData.append('index', index)
                            formData.append('messageId', msg.id)

                            const response =
								await ExecutionReportsService.uploadImage(formData)
                            if (response.data && response.data.url) {
                                return {
                                    ...msg,
                                    urlImageMessage: response.data.url,
                                    urlBase64: msg.urlBase64
                                }
                            } else {
                                throw new Error('Upload response missing image URL')
                            }
                        } catch (error) {
                            console.error('Error uploading message image:', error)
                            throw error // Throw so the sync loop retries the task instead of saving a broken URL
                        }
                    }
                    return msg
                })
            )
            updatedItem.messages = processedMessages
        }

        if (updatedItem.backlogList && updatedItem.backlogList.length > 0) {
            const processedBacklogs = await Promise.all(
                updatedItem.backlogList.map(async (backlog) => {
                    if (backlog && backlog.images && backlog.images.length > 0) {
                        const processedImages = await Promise.all(
                            backlog.images.map(async (img) => {
                                if (
                                    img &&
                                    img.urlBase64 &&
                                    (!img.url ||
                                        img.url.length === 0 ||
                                        img.url.startsWith('blob:') ||
                                        !img.url.startsWith('http'))
                                ) {
                                    try {
                                        const blob = dataURItoBlob(img.urlBase64)
                                        const formData = new FormData()
                                        formData.append('image', blob, `backlog-${Date.now()}.jpg`)
                                        formData.append('reportId', reportId)
                                        formData.append('executionId', executionId)
                                        formData.append('indexGroup', page)
                                        formData.append('index', index)
                                        formData.append('property', 'backlog') // Context marker

                                        const response =
											await ExecutionReportsService.uploadImage(formData)
                                        if (response.data && response.data.url) {
                                            return {
                                                ...img,
                                                url: response.data.url,
                                                urlBase64: img.urlBase64
                                            }
                                        } else {
                                            throw new Error('Upload response missing backlog image URL')
                                        }
                                    } catch (error) {
                                        console.error('Error uploading backlog image:', error)
                                        throw error // Throw so the sync loop retries the task instead of saving a broken URL
                                    }
                                }
                                return img
                            })
                        )
                        return { ...backlog, images: processedImages }
                    }
                    return backlog
                })
            )
            updatedItem.backlogList = processedBacklogs
        }

        return updatedItem
    }

    useEffect(() => {
        if (!location.pathname.includes('assignment/')) {
            setExecutionReport(undefined)
        }
    }, [location])

    const prevIsOnline = useRef(isOnline)

    useEffect(() => {
        // Automatically rely on processQueue effect when connection is restored.
        if (isOnline && !prevIsOnline.current) {
            console.log(
                '[ExecutionContext] Connection restored. Synchronizer will resume automatically.'
            )
        }
        prevIsOnline.current = isOnline
    }, [isOnline])

    const saveItem = async (index, item, page, callback) => {
        let tieneImagen = false

        if (item.messages && item.messages.length > 0) {
            item.messages.forEach((message) => {
                if (
                    (message.urlBase64 && message.urlBase64.length > 0) ||
					(message.urlImageMessage && message.urlImageMessage.length > 0)
                ) {
                    tieneImagen = true
                }
            })
        }

        if (
            item.isChecked &&
			item.isRequerido &&
			(!item.messages || item.messages.length === 0)
        ) {
            alert('Actividad requiere que deje un comentario.')
        } else if (
            item.isChecked &&
			!item.isWarning &&
			item.imagen &&
			!tieneImagen
        ) {
            alert('Actividad requiere que tome al menos una imágen.')
        } else if (
            item.isChecked &&
			!item.isWarning &&
			item.unidad !== '*' &&
			(item.unidadData === null ||
				item.unidadData === undefined ||
				item.unidadData === '')
        ) {
            alert('Actividad requiere que deje una cantidad de insumo utilizado.')
        } else {
            try {
                let updatedItem = {
                    ...item,
                    loading: true,
                    saved: false,
                    isWarning: !!item.isWarning
                }

                if (!updatedItem.historial) {
                    updatedItem.historial = []
                }

                updatedItem.historial.push({
                    tipo: `guardado - ${item.isChecked ? 'marcado' : 'desmarcado'} `,
                    id: Date.now(),
                    fullName: `${userData.name} ${userData.lastName}`
                })

                if (!isOnline) {
                    updatedItem = processImagesForOffline(updatedItem)
                }

                // 1. Prepare updated execution report
                let updatedExecutionReport
                setExecutionReport((prevReport) => {
                    if (!prevReport || !prevReport.group || !prevReport.group[page]) {
                        return prevReport
                    }
                    updatedExecutionReport = {
                        ...prevReport,
                        offLineGuard: Date.now(),
                        group: {
                            ...prevReport.group,
                            [page]: prevReport.group[page].map((it, i) =>
                                i === index ? updatedItem : it
                            )
                        }
                    }
                    return updatedExecutionReport
                })

                // 2. Explicitly persist to IndexedDB (Don't wait for useEffect)
                // We use a small timeout to ensure updatedExecutionReport is populated by the functional state update
                setTimeout(async () => {
                    if (updatedExecutionReport) {
                        console.log(
                            '[ExecutionContext] Explicitly persisting to IndexedDB (Offline mode).'
                        )
                        await executionReportsDatabase.actualizar(updatedExecutionReport)

                        // 3. Update local report progress
                        const newProgress = calculateGlobalProgress(updatedExecutionReport)
                        if (report && report._id) {
                            console.log(
                                `[ExecutionContext] Updating local report progress: ${newProgress}%`
                            )
                            updateLocalReport(report._id, { progress: newProgress })
                        }
                    }
                }, 10)

                if (callback) {
                    callback()
                }

                setSyncQueue((prev) => [
                    ...prev,
                    {
                        item: updatedItem,
                        page,
                        index,
                        reportId: report._id,
                        executionId: executionReport._id
                    }
                ])
                if (!isOnline) {
                    actualizacionEjecucion(Date.now())
                }
            } catch (error) {
                console.error('Error in saveItem logic:', error)
            }
        }
    }

    const calculateGlobalProgress = (execReport) => {
        if (!execReport || !execReport.group) return 0
        let totalItems = 0
        let checkedItems = 0
        Object.values(execReport.group).forEach((groupItems) => {
            if (Array.isArray(groupItems)) {
                groupItems.forEach((item) => {
                    totalItems++
                    if (item && (item.isChecked || item.isWarning)) {
                        checkedItems++
                    }
                })
            }
        })
        return totalItems > 0 ? Math.round((checkedItems * 100) / totalItems) : 0
    }

    const saveExecutionReportToDb = async (executionReport) => {
        const isInspectionOperator =
			isOperator || isUniversalInspector || isUniversalChief
        console.log(
            '[ExecutionContext] saveExecutionReportToDb called. isInspectionOperator:',
            isInspectionOperator
        )
        if (isInspectionOperator) {
            await executionReportsDatabase.actualizar(executionReport)
        }
    }

    useEffect(() => {
        console.log(
            'Execution.context: The value of executionReport has changed to:',
            executionReport
        )
    }, [executionReport])

    useEffect(() => {
        const puedeEnviarCache = permisosEnviar()
        setPuedeEnviar(puedeEnviarCache)
        const puedeCerrarCache = permisosCerrar()
        setPuedeCerrar(puedeCerrarCache)
        const puedeRechazarCache = permisosRechazar()
        setPuedeRechazar(puedeRechazarCache)
        const puedeTerminarJornadaCache = permisosTerminarJornada()
        setPuedeTerminarJornada(puedeTerminarJornadaCache)
    }, [
        report,
        executionReport,
        isOperator,
        isSapExecutive,
        isShiftManager,
        isChiefMachinery,
        isOperatorInspector,
        isUniversalChief,
        isUniversalInspector,
        isObserver
    ])

    useEffect(() => {
        if (executionReport && executionReport.group && typeof executionReport.group === 'object') {
            const group = executionReport.group
            let totalBacklogsCache = 0
            Object.keys(group).forEach((key) => {
                const groupItems = group[key]
                if (Array.isArray(groupItems)) {
                    groupItems.forEach((item) => {
                        if (item && item.backlogList && item.backlogList.length > 0) {
                            totalBacklogsCache = totalBacklogsCache + item.backlogList.length
                        }
                    })
                }
            })
            setTotalBacklogs(totalBacklogsCache)
            saveExecutionReportToDb(executionReport)

            if (otIndex) {
                cachedExecutionsRef.current[otIndex] = {
                    report: report,
                    executionReport: executionReport
                }
            }
        }
    }, [executionReport, report, otIndex])

    const permisosEnviar = () => {
        if (report) {
            const isAdminManager = admin || isSuperAdmin || isOperatorInspector

            // Universal roles: ONLY Inspección
            if (
                isUniversalInspector &&
				report.reportType === 'Inspección' &&
				(!report.level || report.level === 0)
            ) {
                return true
            }
            if (
                isUniversalChief &&
				report.reportType === 'Inspección' &&
				report.level === 1
            ) {
                return true
            }

            if (
                (isOperator || isOperatorInspector) &&
				(!report.level || report.level === 0)
            ) {
                return true
            } else if ((isShiftManager || isAdminManager) && report.level === 1) {
                return true
            } else if ((isChiefMachinery || isAdminManager) && report.level === 2) {
                return report.reportType === 'Mantención'
            } else {
                return false
            }
        }
        return false
    }

    const permisosTerminarJornada = () => {
        if (report) {
            const currentUserId = userData?._id
            const userHasStarted = report.ejecuciones?.some(
                (e) =>
                    (e.userId?._id || e.userId)?.toString() ===
						currentUserId?.toString() && e.termino === null
            )
            const isExecutingOperator = isOperator && !isUniversalChief

            if (
                isExecutingOperator &&
				(!report.level || report.level === 0) &&
				['En proceso', 'Reasignar'].includes(report.state)
            ) {
                return !!userHasStarted
            }
        }
        return false
    }

    const permisosRechazar = () => {
        if (report) {
            const isAdminManager = admin || isSuperAdmin || isOperatorInspector

            // Universal Chief: ONLY Inspección and review levels up to 2
            if (
                isUniversalChief &&
				report.reportType === 'Inspección' &&
				report.level > 0 &&
				report.level < 3
            ) {
                return true
            }

            if ((isShiftManager || isAdminManager) && report.level === 1) {
                return true
            } else if (
                (isChiefMachinery || isAdminManager || isUniversalChief) &&
				report.level === 2
            ) {
                return true
            } else if ((isSapExecutive || isAdminManager) && report.level === 3) {
                return true
            } else {
                return false
            }
        }
        return false
    }

    const permisosCerrar = () => {
        if (report) {
            const isAdminManager = admin || isSuperAdmin || isOperatorInspector

            // Universal Chief: ONLY Inspección and level 2
            if (
                isUniversalChief &&
				report.reportType === 'Inspección' &&
				report.level === 2
            ) {
                return true
            }

            if ((isSapExecutive || isAdminManager) && report.level === 3) {
                return true
            } else if (
                (isChiefMachinery || isAdminManager) &&
				report.level === 2 &&
				report.reportType !== 'Mantención'
            ) {
                return true
            } else {
                return false
            }
        }
        return false
    }

    const getExecutionReport = useCallback(
        async (otIndex) => {
            if (!otIndex) {
                console.log('getExecutionReport: otIndex is missing, cannot fetch.')
                return
            }

            if (currentlyFetchingRef.current === otIndex) {
                console.log(
                    'getExecutionReport: Fetch already in progress for index',
                    otIndex
                )
                return
            }

            currentlyFetchingRef.current = otIndex
            setMessageOnReport('Descargando Pauta...')

            if (cachedExecutionsRef.current[otIndex]) {
                setReport(cachedExecutionsRef.current[otIndex].report)
                setExecutionReport(cachedExecutionsRef.current[otIndex].executionReport)
                setLoading(false)
            } else {
                setReport(null)
                setExecutionReport(null)
                setLoading(true)
            }

            const addUiStateFlags = (execData) => {
                if (!execData || !execData.group) return execData
                const newExecData = { ...execData }
                Object.keys(newExecData.group).forEach((groupKey) => {
                    newExecData.group[groupKey] = newExecData.group[groupKey].map(
                        (item) => {
                            if (!item) return item

                            // Re-hydrate blob URLs if we have base64 data
                            let processedItem = { ...item }
                            processedItem = processImagesForOffline(processedItem)

                            return {
                                ...processedItem,
                                loading: false,
                                saved: item.saved !== false,
                                isWarning: item.isWarning === true
                            }
                        }
                    )
                })
                return newExecData
            }

            try {
                // Step 1: Call the new unified endpoint
                const response = await reportsRoutes.getDetailedReport(otIndex)

                const mainReport = response.data.report
                const executionData = response.data.execution

                if (!mainReport || !mainReport._id) {
                    setMessageOnReport('No se encontró el reporte principal.')
                    setExecutionReport(null)
                    setReport(null)
                    setLoading(false)
                    return
                }

                // Step 2: Set report state
                setReport(mainReport)

                if (!executionData) {
                    const isInspectionOperator =
						isOperator || isOperatorInspector || isUniversalInspector
                    const hasAssignedUsers =
						mainReport.usersAssigned && mainReport.usersAssigned.length > 0
                    const isInProgress = ['En proceso', 'Reasignar'].includes(
                        mainReport.state
                    )

                    let message = ''
                    if (isInspectionOperator) {
                        message = 'Debe iniciar el reporte para acceder al documento.'
                    } else if (isInProgress || hasAssignedUsers) {
                        message = 'El operador aún no ha iniciado la pauta.'
                    } else {
                        message = 'No se encontró la pauta de ejecución para este reporte.'
                    }

                    setMessageOnReport(message)
                    setExecutionReport(null)
                } else {
                    const currentBacklogImagesMap = {}

                    // --- SMART SYNC LOGIC START ---
                    await executionReportsDatabase.initDb()
                    const localExecution = await executionReportsDatabase.obtener(
                        executionData._id
                    )

                    let serverExecutionDataAugmented = executionData
                    if (Object.keys(currentBacklogImagesMap).length > 0) {
                        serverExecutionDataAugmented = injectBase64IntoExecutionReport(
                            executionData,
                            currentBacklogImagesMap
                        )
                    }

                    let useLocalData = false
                    let reason = ''

                    if (localExecution) {
                        // Check for unsaved items in local data
                        let hasUnsavedChanges = false
                        if (localExecution.group) {
                            Object.values(localExecution.group).forEach((items) => {
                                if (items.some((item) => item.saved === false)) {
                                    hasUnsavedChanges = true
                                }
                            })
                        }

                        // Check timestamps (offLineGuard is local save time, updatedAt is server save time)
                        const localTime = new Date(
                            localExecution.offLineGuard || 0
                        ).getTime()
                        const serverTime = new Date(executionData.updatedAt || 0).getTime()

                        const isAssigned =
							userData &&
							mainReport.usersAssigned &&
							mainReport.usersAssigned.some(
							    (u) => u && String(u._id || u) === String(userData._id)
							)

                        if (!isAssigned) {
                            useLocalData = false
                            reason =
								'Usuario no asignado (Admin/Supervisor). Se fuerza la versión del servidor para asegurar datos frescos.'
                        } else if (mainReport.level > 0) {
                            useLocalData = false
                            reason =
								'Reporte ya enviado (Nivel > 0). Se usa la versión del servidor.'
                        } else if (hasUnsavedChanges) {
                            useLocalData = true
                            reason =
								'Cambios locales pendientes de sincronización (saved: false).'
                        } else if (localTime > serverTime) {
                            useLocalData = true
                            reason = `Versión local más reciente (Local: ${localTime} > Server: ${serverTime}).`
                        }
                    }

                    const mergeSignedUrls = (local, server) => {
                        if (!local || !server || !local.group || !server.group) {
                            return local
                        }
                        const merged = { ...local }

                        Object.keys(merged.group).forEach((key) => {
                            const localItems = merged.group[key]
                            const serverItems = server.group[key]

                            if (Array.isArray(localItems) && Array.isArray(serverItems)) {
                                localItems.forEach((lItem, i) => {
                                    const sItem = serverItems[i]
                                    if (!sItem) return

                                    if (lItem.messages && sItem.messages) {
                                        lItem.messages.forEach((lMsg, mI) => {
                                            const sMsg =
												sItem.messages.find((m) => m.id === lMsg.id) ||
												sItem.messages[mI]

                                            if (
                                                sMsg &&
												sMsg.urlImageMessage &&
												sMsg.urlImageMessage.includes('?')
                                            ) {
                                                if (
                                                    lMsg.urlImageMessage &&
													!lMsg.urlImageMessage.includes('?')
                                                ) {
                                                    lMsg.urlImageMessage = sMsg.urlImageMessage
                                                }
                                            }
                                        })
                                    }

                                    if (lItem.backlogList && sItem.backlogList) {
                                        lItem.backlogList.forEach((lBacklog, bI) => {
                                            const sBacklog = sItem.backlogList[bI]

                                            if (sBacklog && lBacklog.images && sBacklog.images) {
                                                lBacklog.images.forEach((lImg, imgI) => {
                                                    const sImg = sBacklog.images[imgI]
                                                    if (sImg && sImg.url && sImg.url.includes('?')) {
                                                        if (lImg.url && !lImg.url.includes('?')) {
                                                            lImg.url = sImg.url
                                                        }
                                                    }
                                                })
                                            }
                                        })
                                    }
                                })
                            }
                        })
                        return merged
                    }

                    if (useLocalData) {
                        let mergedExecution = mergeSignedUrls(
                            localExecution,
                            serverExecutionDataAugmented
                        ) // Merge with augmented server data
                        if (Object.keys(currentBacklogImagesMap).length > 0) {
                            mergedExecution = injectBase64IntoExecutionReport(
                                mergedExecution,
                                currentBacklogImagesMap
                            ) // Inject into merged local as well
                        }
                        const processedLocalData = addUiStateFlags(mergedExecution)
                        setExecutionReport(processedLocalData)

                        cachedExecutionsRef.current[otIndex] = {
                            report: mainReport,
                            executionReport: processedLocalData
                        }

                        // Trigger sync attempt if online
                        if (isOnline) {
                            console.log(
                                '[Sync] ✅ Usando data local (Cambios pendientes). El sincronizador los procesará automáticamente.'
                            )
                        }
                    } else {
                        console.log('[Sync] ✅ Usando data del servidor (Sincronizada).')
                        const processedData = addUiStateFlags(serverExecutionDataAugmented) // Use augmented server data here
                        setExecutionReport(processedData)
                        await executionReportsDatabase.actualizar(processedData)
                        setMessageOnReport('')

                        cachedExecutionsRef.current[otIndex] = {
                            report: mainReport,
                            executionReport: processedData
                        }
                    }
                    // --- SMART SYNC LOGIC END ---
                }
            } catch (error) {
                console.error('getExecutionReport failed (Offline or Error):', error)

                // Offline Fallback
                try {
                    await executionReportsDatabase.initDb()
                    await reportsDatabase.initDbReports()

                    const targetIdx = Number(otIndex)

                    // --- ROBUST OFFLINE RECOVERY START ---
                    const dbReport = await reportsDatabase.obtener(targetIdx)
                    if (dbReport) {
                        console.log(
                            '[Offline] Refreshing report state from local DB:',
                            dbReport._id
                        )

                        const currentUserId = userData?._id
                        const memHasStarted = report?.ejecuciones?.some(
                            (e) =>
                                (e.userId?._id || e.userId)?.toString() ===
									currentUserId?.toString() && e.termino === null
                        )
                        const dbHasStarted = dbReport.ejecuciones?.some(
                            (e) =>
                                (e.userId?._id || e.userId)?.toString() ===
									currentUserId?.toString() && e.termino === null
                        )

                        if (
                            !memHasStarted ||
							dbHasStarted ||
							String(report?._id) !== String(dbReport._id)
                        ) {
                            setReport(dbReport)
                        } else {
                            console.log(
                                '[Offline] Preserving in-memory report since it has ongoing executions while DB version does not.'
                            )
                            await reportsDatabase.actualizar(report)
                        }
                    }

                    const currentReport = dbReport || report

                    const allExecutions = await executionReportsDatabase.consultarTodas()

                    // Strategy: Find execution linked to this index in either field
                    const localExec = allExecutions.find((e) => {
                        // 1. Check if execution has populated report object with matching index
                        if (e.report && typeof e.report === 'object') {
                            if (
                                String(e.report.newIdIndex || e.report.idIndex) ===
								String(targetIdx)
                            ) {
                                return true
                            }
                        }

                        // 2. Check by report ID (if we found the report locally or in context)
                        if (currentReport) {
                            const targetReportId = String(currentReport._id)
                            const eReportId = e.reportId
                                ? String(e.reportId)
                                : e.report && typeof e.report === 'string'
                                    ? e.report
                                    : e.report?._id
                                        ? String(e.report._id)
                                        : null
                            if (eReportId === targetReportId) return true
                        }

                        // 3. Last resort: check if any index matches (some old executions might have it)
                        if (String(e.newIdIndex || e.idIndex) === String(targetIdx)) {
                            return true
                        }

                        return false
                    })
                    // --- ROBUST OFFLINE RECOVERY END ---

                    if (localExec) {
                        console.log(
                            '[Offline] Cargando ejecución desde IndexedDB:',
                            localExec._id
                        )
                        const processedLocalData = addUiStateFlags(localExec)
                        setExecutionReport(processedLocalData)
                        setMessageOnReport('')

                        cachedExecutionsRef.current[otIndex] = {
                            report: currentReport,
                            executionReport: processedLocalData
                        }
                    } else {
                        const errorMsg = isOnline
                            ? 'Error al descargar la pauta del servidor. Verifique su conexión o intente más tarde.'
                            : 'Modo Offline: No se encontró la pauta en el dispositivo.'
                        setMessageOnReport(errorMsg)
                    }
                } catch (dbError) {
                    console.error('Error querying local DB in fallback:', dbError)
                    setMessageOnReport('Error crítico al cargar datos (Local/Server).')
                }
            } finally {
                currentlyFetchingRef.current = null
                setLoading(false)
            }
        },
        [
            setReport,
            setMessageOnReport,
            setLoading,
            setExecutionReport,
            isUniversalInspector,
            isUniversalChief
        ]
    )

    const selectionItem = (value = new String()) => {
        const state = detectIf3DModelExist(value, machineData.model)
        const system = {
            name: translateSubSystem(value),
            brand: machineData.brand,
            model: machineData.model,
            nameModel: `${translateSubSystem(value)}_${machineData.model}`
        }
        setSubSistem(JSON.stringify(system))
        setHabilite3D(state)
    }

    const endReport = async (text) => {
        setMessageType(text === 'aprobado' ? 'sendReport' : 'rejectReport')
        setIsTermJornada(false)
        setOpenReportCommitModal(true)
        setOpenMenu(false)
    }

    const mensajeTerminoJornada = () => {
        setIsTermJornada(true)
        setOpenReportCommitModal(true)
        setOpenMenu(false)
    }

    const forwardReport = () => {
        if (isOnline) {
            setToForward(true)
            endReport('aprobado')
        } else {
            alert('Dispositivo debe estar conectado a internet.')
        }
        setOpenMenu(false)
    }

    const rejectReport = () => {
        if (isOnline) {
            setToForward(false)
            endReport('rechazado')
        } else {
            alert('Dispositivo debe estar conectado a internet.')
        }
        setOpenMenu(false)
    }

    const vistaPrevia = async () => {
        setOpenMenu(false)
        if (
            (report.level && report.level > 0) ||
			isOperator ||
			admin ||
			isSuperAdmin ||
			isShiftManager
        ) {
            if (executionReport && executionReport.group) {
                const listaMateriales = []
                Object.keys(executionReport.group).forEach((key) => {
                    const groupItems = executionReport.group[key]
                    const hoja = {
                        nombre: key,
                        materialespreview: []
                    }
                    groupItems.forEach((item) => {
                        if (item.unidad !== '*') {
                            const images = []
                            if (item.messages && item.messages.length > 0) {
                                item.messages.forEach((msg) => {
                                    if (msg.urlImageMessage) images.push(msg.urlImageMessage)
                                    else if (msg.urlBase64) images.push(msg.urlBase64)
                                })
                            }

                            const materialData = {
                                task: item.task,
                                taskdesc: item.taskdesc,
                                obs01: item.obs01,
                                partnumberUtl: item.partnumberUtl || item.partnumber_utl,
                                cantidad: item.cantidad,
                                unidad: item.unidad,
                                unidadData: item.unidadData ? Number(item.unidadData) : 0,
                                diferencia:
									parseFloat(item.cantidad.toString()) -
									parseFloat(
									    item.unidadData ? item.unidadData.toString() : '0'
									),
                                images: images
                            }
                            hoja.materialespreview.push(materialData)
                        }
                    })
                    if (hoja.materialespreview.length > 0) {
                        listaMateriales.push(hoja)
                    }
                })
                setMaterialesPreview(listaMateriales)
                setOpenPreviewModal(true)
            } else {
                alert('No se encontraron datos de ejecución para generar el resumen.')
            }
        } else {
            alert(
                'Orden aún no está listo para revisar el listado de materiales o insumos.'
            )
        }
    }

    const openMessages = useCallback(() => {
        if (report) {
            setOpenMenu(false)
            setOpenMessagesModal(true)
        } else {
            console.error('Report data is not available, cannot open messages.')
        }
    }, [report, setOpenMessagesModal, setOpenMenu])

    const closeMessages = () => {
        setOpenMessagesModal(false)
    }

    const continuarTerminarJornada = async (data) => {
        if (isOnline) {
            console.log('[ExecutionContext] Finishing shift with data:', data)
            setLoadingLogo(true)
            try {
                const response = await ExecutionReportsService.finishShift(
                    executionReport._id,
                    {
                        comentario: data?.comentario,
                        operarios: data?.operarios
                    }
                )
                console.log('[ExecutionContext] Finish shift response:', response)

                // Cleanup local execution data
                console.log(
                    `[ExecutionContext] Cleaning up local execution data after finishShift: ${executionReport._id}`
                )
                await executionReportsDatabase.eliminar(executionReport._id)

                setstatus({
                    status: 'completed',
                    title: 'Jornada terminada',
                    message: 'Reporte enviado para reasignación',
                    severity: 'success'
                })

                await getReports()
                navigate('/assignment')
            } catch (error) {
                console.error('[ExecutionContext] Error ending shift:', error)
                setstatus({
                    status: 'error',
                    message: 'Error al terminar la jornada.',
                    errorDetails: {
                        code: 'ERR_FINISH_SHIFT',
                        info: error.response?.data?.message || error.message
                    }
                })
            } finally {
                setLoadingLogo(false)
                setIsTermJornada(false)
                setOpenReportCommitModal(false)
            }
        } else {
            alert(
                'Dispositivo no está conectado a internet. Conecte a una red e intente nuevamente'
            )
        }
    }

    const downloadFile = async (url, filename) => {
        try {
            const response = await fetch(url)
            if (!response.ok) throw new Error('Network response was not ok')
            const blob = await response.blob()
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Download failed:', error)
        }
    }

    const closeCommitModalToForward = async (data) => {
        setOpenReportCommitModal(false)
        if (data) {
            if (
                isOperator &&
				report &&
				report.usersAssigned &&
				report.usersAssigned.length > 1
            ) {
                const confirmClose = window.confirm(
                    'Atención: Estás a punto de cerrar un reporte y aún hay otros usuarios asociados a este reporte. ' +
						'¿Deseas terminar el reporte? Si aceptas, se expulsará a los demás usuarios asignados.'
                )
                if (!confirmClose) {
                    return
                }
            }

            console.log('[ExecutionContext] Sending OT with data:', data)

            setEjecucion({
                inicio: null,
                termino: null,
                userId: null
            })

            setLoadingLogo(true)
            setLoadingMessage('Guardando información')

            try {
                if (executionReport && executionReport._id) {
                    console.log('[ExecutionContext] Calling completeExecutionReport...')
                    await ExecutionReportsService.completeExecutionReport(
                        executionReport._id,
                        {
                            comentario: data.comentario,
                            operarios: data.operarios
                        }
                    )
                }

                setstatus({
                    status: 'completed',
                    title: isOperator ? 'OT Enviada' : 'Reporte Aprobado',
                    message: isOperator
                        ? 'La orden de trabajo ha sido enviada exitosamente.'
                        : 'Se ha pasado al siguiente nivel correctamente.',
                    severity: isOperator ? 'success' : 'info',
                    isSummary: false
                })

                const isManagerialRole =
					admin ||
					isSuperAdmin ||
					isSapExecutive ||
					isShiftManager ||
					isChiefMachinery ||
					isOperatorInspector ||
					isUniversalChief ||
					isUniversalInspector

                if (isUniversalInspector) {
                    navigate('/assignment')
                } else if (isOperator && !isManagerialRole) {
                    navigate('/welcome')
                } else {
                    navigate('/reports')
                }

                const isInspectionAutoClose =
					(isChiefMachinery || admin || isOperatorInspector) &&
					report.level === 2 &&
					report.reportType !== 'Mantención'
                if (
                    ((isSapExecutive || admin || isOperatorInspector) &&
						report.level === 3) ||
					isInspectionAutoClose
                ) {
                    setTimeout(() => {
                        console.log(
                            '[ExecutionContext] Initiating non-blocking PDF generation.'
                        )
                        initiatePdfGeneration(report)
                    }, 100)
                }

                await getReports()
            } catch (error) {
                console.error('[ExecutionContext] Error closing report:', error)
                setstatus({
                    status: 'error',
                    message: 'Error al enviar/cerrar el reporte.',
                    errorDetails: {
                        code: 'ERR_CLOSE',
                        info: error.response?.data?.message || error.message
                    }
                })
            } finally {
                setLoadingLogo(false)
            }
        }
    }

    const closeCommitModalToBack = async (data) => {
        setOpenReportCommitModal(false)
        if (data) {
            console.log('[ExecutionContext] Rejecting OT with data:', data)

            try {
                setLoadingLogo(true)

                const updatedReport = ReportsService.processReportData({
                    report,
                    progress: executionReport.progress,
                    messageType: 'rejectReport',
                    data,
                    userData
                })

                updatedReport.message = data.comentario

                console.log(
                    '[ExecutionContext] Sending rejected report to backend:',
                    updatedReport
                )

                await elevarReporte(updatedReport)

                // If successful:
                await getReports()
                setstatus({
                    status: 'completed',
                    title: 'Reporte Rechazado',
                    message: 'Retornando al nivel anterior.',
                    severity: 'warning',
                    isSummary: false
                })
                navigate('/reports')
            } catch (error) {
                console.error('[ExecutionContext] Error rejecting report:', error)
                setstatus({
                    status: 'error',
                    message: 'Error al rechazar el reporte.',
                    errorDetails: { code: 'ERR_REJECT', info: error.message }
                })
            } finally {
                setLoadingLogo(false)
            }
        }
    }

    const handleCommitModalResult = (data) => {
        if (isTermJornada) {
            setOpenReportCommitModal(false)
            setIsTermJornada(false)
        } else if (toForward) {
            closeCommitModalToForward(data)
        } else {
            closeCommitModalToBack(data)
        }
    }

    const startReport = useCallback(
        async (reportId, reportObject = null) => {
            if (!reportId) return null

            const targetReport =
				reportObject || (reports && reports.find((r) => r._id === reportId))

            try {
                if (isOnline) {
                    console.log(`[ExecutionContext] Starting report ${reportId} ONLINE.`)
                    const response =
						await executionReportsRoutes.startExecutionReport(reportId)
                    const executionData = response.data

                    let updatedEjecuciones = executionData.report?.ejecuciones
                    if (!updatedEjecuciones || updatedEjecuciones.length === 0) {
                        const newEjecucion = {
                            userId: userData,
                            inicio: executionData.startDate || new Date(),
                            termino: null
                        }
                        const currentEjecuciones = targetReport?.ejecuciones || []
                        updatedEjecuciones = [...currentEjecuciones, newEjecucion]
                    }

                    const isAssigned = targetReport?.usersAssigned?.some(
                        (u) => (u?._id || u)?.toString() === userData?._id?.toString()
                    )

                    const updates = {
                        dateInit: executionData.startDate || new Date(),
                        state: 'En proceso',
                        ejecuciones: updatedEjecuciones
                    }

                    if (!isAssigned) {
                        updates.usersAssigned = [
                            ...(targetReport?.usersAssigned || []),
                            userData
                        ]
                        const lastId = targetReport?.historialAsignaciones?.length > 0
                            ? targetReport.historialAsignaciones[targetReport.historialAsignaciones.length - 1].id
                            : 0
                        const currentId = (lastId ?? 0) + 1
                        updates.historialAsignaciones = [
                            ...(targetReport?.historialAsignaciones || []),
                            {
                                asignadoA: userData,
                                asignadoPor: userData,
                                id: currentId,
                                fecha: new Date()
                            }
                        ]
                    }

                    updateLocalReport(reportId, updates)

                    return executionData
                } else {
                    console.log(
                        `[ExecutionContext] Starting report ${reportId} OFFLINE. Adding to queue.`
                    )

                    setSyncQueue((prev) => {
                        const exists = prev.some(
                            (q) => q.type === 'start' && q.reportId === reportId
                        )
                        if (exists) return prev
                        return [...prev, { type: 'start', reportId }]
                    })

                    const newEjecucion = {
                        userId: userData,
                        inicio: new Date(),
                        termino: null
                    }

                    const currentEjecuciones = targetReport?.ejecuciones || []
                    const updatedEjecuciones = [...currentEjecuciones, newEjecucion]

                    const isAssigned = targetReport?.usersAssigned?.some(
                        (u) => (u?._id || u)?.toString() === userData?._id?.toString()
                    )

                    const updates = {
                        dateInit: targetReport?.dateInit || new Date(),
                        state: 'En proceso',
                        ejecuciones: updatedEjecuciones
                    }

                    if (!isAssigned) {
                        updates.usersAssigned = [
                            ...(targetReport?.usersAssigned || []),
                            userData
                        ]
                        const lastId = targetReport?.historialAsignaciones?.length > 0
                            ? targetReport.historialAsignaciones[targetReport.historialAsignaciones.length - 1].id
                            : 0
                        const currentId = (lastId ?? 0) + 1
                        updates.historialAsignaciones = [
                            ...(targetReport?.historialAsignaciones || []),
                            {
                                asignadoA: userData,
                                asignadoPor: userData,
                                id: currentId,
                                fecha: new Date()
                            }
                        ]
                    }

                    await updateLocalReport(reportId, updates)
                    return null
                }
            } catch (error) {
                console.error('[ExecutionContext] Error in startReport:', error)
                throw error
            }
        },
        [isOnline, reports, updateLocalReport, userData]
    )

    const bulkAssignReports = useCallback(
        async (reportIds) => {
            if (!isOnline) {
                setstatus({
                    status: 'error',
                    message: 'Debe estar conectado a internet para preparar las pautas.'
                })
                return
            }

            setLoading(true)
            setLoadingMessage('Asignando pautas...')
            setstatus({
                status: 'generating',
                message: 'Preparando pautas (auto-asignación)...'
            })

            try {
                console.log(
                    `[ExecutionContext] Bulk ASSIGNING ${reportIds.length} reports.`
                )
                const syncResults =
					await ExecutionReportsService.bulkAssignExecutionReports(reportIds)

                setstatus({
                    status: 'completed',
                    title: 'Preparación completa',
                    message: `${syncResults.length} pautas asignadas exitosamente.`,
                    severity: 'success'
                })

                await getReports()
            } catch (error) {
                console.error('[ExecutionContext] Error in bulkAssignReports:', error)
                setstatus({
                    status: 'error',
                    message: 'Error al asignar pautas masivamente.',
                    errorDetails: {
                        code: 'ERR_BULK_ASSIGN',
                        info: error.response?.data?.message || error.message
                    }
                })
            } finally {
                setLoading(false)
                setLoadingMessage('')
            }
        },
        [isOnline, getReports, setstatus]
    )

    const bulkSyncExecutionReports = useCallback(
        async (reportIds, reportObjects = []) => {
            if (!isOnline) {
                setstatus({
                    status: 'error',
                    message: 'Debe estar conectado a internet para descargar las pautas.'
                })
                return
            }

            setLoading(true)
            setLoadingMessage('Sincronizando pautas...')
            cancelDownloadRef.current = false

            // Iniciar barra de descarga en Header (sin abrir statusmodal aún)
            setIsDownloadingPautas(true)
            setDownloadPautasProgress(5)
            setDownloadPautasStatusText('Iniciando descarga de pautas...')

            try {
                console.log(
                    `[ExecutionContext] Bulk PREPARING ${reportIds.length} reports.`
                )

                // Inicializar bases de datos locales
                await executionReportsDatabase.initDb()
                await reportsDatabase.initDbReports()
                await documentationDatabase.initDbDocumentation()

                // Fase 1: Descargar reportes
                setDownloadPautasProgress(15)
                setDownloadPautasStatusText('Obteniendo información de pautas...')
                const syncResults =
					await ExecutionReportsService.bulkPrepareExecutionReports(reportIds)

                setDownloadPautasProgress(25)
                setDownloadPautasStatusText('Procesando y guardando reportes...')

                let reportsSavedCount = 0
                const totalReportsToSync = syncResults.length

                for (const result of syncResults) {
                    if (cancelDownloadRef.current || !navigator.onLine) {
                        const isOfflineCancel = !navigator.onLine
                        console.log(
                            '[ExecutionContext] Download interrupted during report saving loop. Offline:',
                            isOfflineCancel
                        )
                        setstatus({
                            status: 'completed',
                            title: isOfflineCancel
                                ? 'Conexión perdida'
                                : 'Descarga cancelada',
                            message: `Reportes descargados: ${reportsSavedCount}, documentos: 0.`,
                            severity: 'warning'
                        })
                        cancelDownloadRef.current = false
                        setIsDownloadingPautas(false)
                        setDownloadPautasProgress(0)
                        setDownloadPautasStatusText('')
                        await getReports()
                        return
                    }

                    const { execution, images } = result
                    if (!execution) continue

                    const backlogImagesMap = images.reduce((acc, imgInfo) => {
                        acc[cleanAzureUrl(imgInfo.imageUrl)] = imgInfo.base64
                        return acc
                    }, {})

                    const augmentedExecution = injectBase64IntoExecutionReport(
                        execution,
                        backlogImagesMap
                    )
                    await executionReportsDatabase.actualizar(augmentedExecution)

                    const mainReportId = execution.report?._id || execution.report
                    if (mainReportId) {
                        const reportIdStr = String(mainReportId)
                        let mainReport = reports.find((r) => String(r._id) === reportIdStr)

                        if (!mainReport && reportObjects.length > 0) {
                            mainReport = reportObjects.find(
                                (r) => String(r._id) === reportIdStr
                            )
                        }

                        if (mainReport) {
                            console.log(
                                `[ExecutionContext] Persisting main report metadata for OT ${mainReport.newIdIndex || mainReport.idIndex}`
                            )
                            await reportsDatabase.actualizar(mainReport)
                        } else {
                            console.warn(
                                `[ExecutionContext] Could not find report metadata for ID ${reportIdStr} to save in IndexedDB.`
                            )
                        }
                    }

                    reportsSavedCount++
                    const progressVal = Math.round(
                        25 + (reportsSavedCount / totalReportsToSync) * 5
                    )
                    setDownloadPautasProgress(progressVal)
                    setDownloadPautasStatusText(
                        `Sincronizando reporte ${reportsSavedCount} de ${totalReportsToSync}...`
                    )
                }

                // Fin Fase 1
                setDownloadPautasProgress(30)
                setDownloadPautasStatusText(
                    'Reportes guardados. Iniciando descarga de documentos...'
                )

                // Desbloquear UI del operador para trabajar offline inmediatamente
                setLoading(false)
                setLoadingMessage('')
                setstatus({
                    status: 'completed',
                    title: 'Pautas disponibles',
                    message: `Reportes descargados: ${syncResults.length}, documentos descargando en segundo plano.`,
                    severity: 'success'
                })

                // Fase 2: Descargar documentos técnicos vinculados a los SPM
                downloadAbortControllerRef.current = new AbortController()
                const spmGuidesMap = new Map() // spmCode -> Set of guideNames
                for (const reportObj of reportObjects) {
                    if (reportObj.idPm && reportObj.hasTechnicalDocs) {
                        const normalizedSpm = reportObj.idPm.toUpperCase().trim()
                        const guideName = reportObj.guide ? reportObj.guide.toUpperCase().trim() : ''
                        if (!spmGuidesMap.has(normalizedSpm)) {
                            spmGuidesMap.set(normalizedSpm, new Set())
                        }
                        if (guideName) {
                            spmGuidesMap.get(normalizedSpm).add(guideName)
                        }
                    }
                }

                const spmStructures = []
                if (spmGuidesMap.size > 0) {
                    setDownloadPautasStatusText('Obteniendo listado de documentos...')
                }

                for (const [spmCode, guidesSet] of spmGuidesMap.entries()) {
                    if (cancelDownloadRef.current || !navigator.onLine) {
                        const isOfflineCancel = !navigator.onLine
                        console.log(
                            '[ExecutionContext] Download interrupted before fetching structures. Offline:',
                            isOfflineCancel
                        )
                        setstatus({
                            status: 'completed',
                            title: isOfflineCancel
                                ? 'Conexión perdida'
                                : 'Descarga cancelada',
                            message: `Reportes descargados: ${syncResults.length}, documentos: 0.`,
                            severity: 'warning'
                        })
                        cancelDownloadRef.current = false
                        setIsDownloadingPautas(false)
                        setDownloadPautasProgress(0)
                        setDownloadPautasStatusText('')
                        await getReports()
                        return
                    }

                    try {
                        console.log(
                            `[ExecutionContext] Downloading technical documentation structure for SPM: ${spmCode}`
                        )
                        const docRes = await documentsRoutes.getSpmDocumentSteps(spmCode)
                        const spmDocData = docRes.data
                        if (spmDocData && spmDocData.steps) {
                            const filteredSteps = spmDocData.steps.filter((step) =>
                                guidesSet.has(step.stepName.toUpperCase().trim())
                            )
                            spmStructures.push({
                                ...spmDocData,
                                steps: filteredSteps
                            })
                        }
                    } catch (docError) {
                        console.error(
                            `[ExecutionContext] Failed to fetch doc structure for SPM ${spmCode}:`,
                            docError
                        )
                    }
                }

                // Compilar lista de archivos a descargar
                const filesToDownload = []
                for (const structure of spmStructures) {
                    for (const step of structure.steps) {
                        if (step.files) {
                            for (const file of step.files) {
                                if (file.url) {
                                    filesToDownload.push({
                                        spmStructure: structure,
                                        file: file
                                    })
                                }
                            }
                        }
                    }
                }

                const totalFiles = filesToDownload.length
                if (totalFiles > 0) {
                    // Save each unique SPM structure once before the file loop
                    const savedSpms = new Set()
                    for (const item of filesToDownload) {
                        const spmCode = item.spmStructure.spm
                        if (!savedSpms.has(spmCode)) {
                            savedSpms.add(spmCode)
                            await documentationDatabase.saveSpmStructure(item.spmStructure)
                        }
                    }

                    // Increment generation counter to invalidate any old in-flight downloads
                    downloadGenerationRef.current += 1
                    const currentGeneration = downloadGenerationRef.current

                    let downloadedCount = 0
                    for (const item of filesToDownload) {
                        if (
                            cancelDownloadRef.current ||
							currentGeneration !== downloadGenerationRef.current ||
							!navigator.onLine
                        ) {
                            const isOfflineCancel = !navigator.onLine
                            console.log(
                                '[ExecutionContext] Download interrupted during file loop. Offline:',
                                isOfflineCancel
                            )
                            setstatus({
                                status: 'completed',
                                title: isOfflineCancel
                                    ? 'Conexión perdida'
                                    : 'Descarga cancelada',
                                message: `Reportes descargados: ${syncResults.length}, documentos: ${downloadedCount}.`,
                                severity: 'warning'
                            })
                            cancelDownloadRef.current = false
                            setIsDownloadingPautas(false)
                            setDownloadPautasProgress(0)
                            setDownloadPautasStatusText('')
                            await getReports()
                            return
                        }

                        const { file, spmStructure } = item

                        try {
                            console.log(
                                `[ExecutionContext] Downloading file ${file.filename} for SPM ${spmStructure.spm}`
                            )
                            const fileResponse = await fetch(file.url, {
                                signal: downloadAbortControllerRef.current.signal
                            })
                            if (fileResponse.ok) {
                                const fileBlob = await fileResponse.blob()
                                await documentationDatabase.savePdfFile({
                                    blobPath: file.blobPath,
                                    blob: fileBlob
                                })
                                console.log(
                                    `[ExecutionContext] Saved file ${file.filename} offline.`
                                )
                            }
                        } catch (fileError) {
                            console.error(
                                `[ExecutionContext] Failed to download file ${file.filename}:`,
                                fileError
                            )
                        }

                        downloadedCount++
                        const fileProgressFraction = downloadedCount / totalFiles
                        const progress = Math.round(30 + fileProgressFraction * 70)
                        setDownloadPautasProgress(progress)
                        setDownloadPautasStatusText(
                            `Descargando documento ${downloadedCount} de ${totalFiles}...`
                        )
                    }
                }

                setDownloadPautasProgress(100)
                setDownloadPautasStatusText('Descarga completa')

                setstatus({
                    status: 'completed',
                    title: 'Descarga completa',
                    message: `Reportes descargados: ${syncResults.length}, documentos: ${totalFiles}.`,
                    severity: 'success'
                })

                // Ocultar barra en Header después de 1.5s
                if (hideTimerRef.current) {
                    clearTimeout(hideTimerRef.current)
                }
                hideTimerRef.current = setTimeout(() => {
                    hideTimerRef.current = null
                    setIsDownloadingPautas(false)
                    setDownloadPautasProgress(0)
                    setDownloadPautasStatusText('')
                }, 1500)

                await getReports()
            } catch (error) {
                console.error(
                    '[ExecutionContext] Error in bulkSyncExecutionReports:',
                    error
                )
                setstatus({
                    status: 'error',
                    message: 'Error al preparar pautas masivamente.',
                    errorDetails: {
                        code: 'ERR_BULK_PREPARE',
                        info: error.response?.data?.message || error.message
                    }
                })
                await getReports()
                setIsDownloadingPautas(false)
                setDownloadPautasProgress(0)
                setDownloadPautasStatusText('')
            } finally {
                setLoading(false)
                setLoadingMessage('')
            }
        },
        [isOnline, reports, updateLocalReport, getReports, setstatus]
    )

    // Track and cleanup blob URLs for offline images to prevent memory leaks
    useEffect(() => {
        if (!executionReport) return

        const currentUrls = new Set()
        if (executionReport.group) {
            Object.values(executionReport.group).forEach((items) => {
                if (Array.isArray(items)) {
                    items.forEach((item) => {
                        if (!item) return
                        if (item.messages) {
                            item.messages.forEach((msg) => {
                                if (msg?.urlImageMessage?.startsWith('blob:')) {
                                    currentUrls.add(msg.urlImageMessage)
                                }
                            })
                        }
                        if (item.backlogList) {
                            item.backlogList.forEach((backlog) => {
                                if (backlog?.images) {
                                    backlog.images.forEach((img) => {
                                        if (img?.url?.startsWith('blob:')) {
                                            currentUrls.add(img.url)
                                        }
                                    })
                                }
                            })
                        }
                    })
                }
            })
        }

        // Revoke URLs no longer in use
        blobUrlsRef.current.forEach((url) => {
            if (!currentUrls.has(url)) {
                try {
                    URL.revokeObjectURL(url)
                } catch (_) {}
            }
        })

        blobUrlsRef.current = currentUrls

        return () => {
            blobUrlsRef.current.forEach((url) => {
                try {
                    URL.revokeObjectURL(url)
                } catch (_) {}
            })
            blobUrlsRef.current.clear()
        }
    }, [executionReport])

    useEffect(() => {
        let timerId = null

        const fetchImagesInBackground = async () => {
            const reportId = executionReport?._id
            if (!reportId || !isOnline) return

            // Si ya se descargaron las imágenes para esta ejecución en esta sesión, omitir
            if (downloadedImagesIdRef.current.has(reportId)) return

            // Si el reporte no tiene imágenes que requieran descarga, marcar como descargadas y omitir
            if (!hasExecutionReportImages(executionReport)) {
                console.log(
                    '[ExecutionContext] No images found in execution report, skipping fetch.'
                )
                downloadedImagesIdRef.current.add(reportId)
                return
            }

            try {
                setLoadingImages(true)
                const fetchedBacklogImagesArray =
					await ExecutionReportsService.getExecutionImages(reportId)
                if (fetchedBacklogImagesArray && fetchedBacklogImagesArray.length > 0) {
                    const currentBacklogImagesMap = fetchedBacklogImagesArray.reduce(
                        (acc, imgInfo) => {
                            acc[cleanAzureUrl(imgInfo.imageUrl)] = imgInfo.base64
                            return acc
                        },
                        {}
                    )

                    setExecutionReport((prevReport) => {
                        if (!prevReport || prevReport._id !== reportId) return prevReport
                        return injectBase64IntoExecutionReport(
                            prevReport,
                            currentBacklogImagesMap
                        )
                    })

                    setBacklogImages((prev) => ({
                        ...prev,
                        ...currentBacklogImagesMap
                    }))
                }
                downloadedImagesIdRef.current.add(reportId)
            } catch (err) {
                console.error(
                    '[CONTEXT] Failed to fetch backlog images in background:',
                    err
                )
            } finally {
                setLoadingImages(false)
            }
        }

        if (executionReport?._id && isOnline) {
            timerId = setTimeout(fetchImagesInBackground, 2000)
        }

        return () => {
            if (timerId) {
                clearTimeout(timerId)
            }
        }
    }, [executionReport?._id, isOnline])

    const exportLocalReportData = useCallback(async (reportIdTarget) => {
        try {
            await executionReportsDatabase.initDb()
            await reportsDatabase.initDbReports()

            const targetId = String(reportIdTarget || report?._id || '')

            let localExecReport = null
            if (targetId) {
                localExecReport = await executionReportsDatabase.consultar(targetId)
                if (!localExecReport) {
                    localExecReport = await executionReportsDatabase.obtener(targetId)
                }
            }
            if (!localExecReport && executionReport) {
                localExecReport = executionReport
            }

            let parentReport = null
            if (targetId) {
                parentReport = await reportsDatabase.obtener(targetId)
            }
            if (!parentReport && report) {
                parentReport = report
            }

            if (!parentReport && !localExecReport) {
                alert('No se encontraron datos locales para este reporte.')
                return false
            }

            const processImagesInGroup = async (groupObj) => {
                if (!groupObj) return groupObj
                const clonedGroup = JSON.parse(JSON.stringify(groupObj))

                for (const pageKey in clonedGroup) {
                    const items = clonedGroup[pageKey]
                    if (Array.isArray(items)) {
                        for (let i = 0; i < items.length; i++) {
                            const item = items[i]
                            if (!item) continue

                            if (item.messages && Array.isArray(item.messages)) {
                                for (let m = 0; m < item.messages.length; m++) {
                                    const msg = item.messages[m]
                                    if (msg && msg.urlImageMessage && msg.urlImageMessage.startsWith('blob:')) {
                                        try {
                                            const res = await fetch(msg.urlImageMessage)
                                            const blob = await res.blob()
                                            const base64 = await new Promise((resolve) => {
                                                const r = new FileReader()
                                                r.onloadend = () => resolve(r.result)
                                                r.onerror = () => resolve(null)
                                                r.readAsDataURL(blob)
                                            })
                                            if (base64) msg.urlBase64 = base64
                                        } catch (e) {
                                            console.error('Error converting blob message image:', e)
                                        }
                                    }
                                }
                            }

                            if (item.backlogList && Array.isArray(item.backlogList)) {
                                for (let b = 0; b < item.backlogList.length; b++) {
                                    const backlog = item.backlogList[b]
                                    if (backlog && backlog.images && Array.isArray(backlog.images)) {
                                        for (let imgIdx = 0; imgIdx < backlog.images.length; imgIdx++) {
                                            const img = backlog.images[imgIdx]
                                            if (img && img.url && img.url.startsWith('blob:')) {
                                                try {
                                                    const res = await fetch(img.url)
                                                    const blob = await res.blob()
                                                    const base64 = await new Promise((resolve) => {
                                                        const r = new FileReader()
                                                        r.onloadend = () => resolve(r.result)
                                                        r.onerror = () => resolve(null)
                                                        r.readAsDataURL(blob)
                                                    })
                                                    if (base64) img.urlBase64 = base64
                                                } catch (e) {
                                                    console.error('Error converting blob backlog image:', e)
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                return clonedGroup
            }

            const processedGroup = localExecReport?.group ? await processImagesInGroup(localExecReport.group) : null

            const baseReport = parentReport ? JSON.parse(JSON.stringify(parentReport)) : (localExecReport ? JSON.parse(JSON.stringify(localExecReport)) : {})

            const exportPayload = { ...baseReport }

            if (exportPayload.execution && typeof exportPayload.execution === 'object') {
                if (processedGroup) {
                    exportPayload.execution.group = processedGroup
                }
            } else if (exportPayload.executionReport && typeof exportPayload.executionReport === 'object') {
                if (processedGroup) {
                    exportPayload.executionReport.group = processedGroup
                }
            } else if (localExecReport) {
                exportPayload.execution = {
                    ...JSON.parse(JSON.stringify(localExecReport)),
                    ...(processedGroup ? { group: processedGroup } : {})
                }
            }

            delete exportPayload.group

            const ot = baseReport.newIdIndex || baseReport.idIndex || baseReport._id || targetId || '0'
            const sap = baseReport.sapId || '0'
            let tipoPauta = 'PI'
            if (baseReport.reportType === 'Inspección' || baseReport.type === 'Inspección') {
                tipoPauta = 'PI'
            } else if (baseReport.guide) {
                tipoPauta = baseReport.guide
            } else if (baseReport.reportType) {
                tipoPauta = baseReport.reportType
            }

            const fileName = `${ot}_${sap}_${tipoPauta}.json`

            const jsonStr = JSON.stringify(exportPayload, null, 2)
            const blob = new Blob([jsonStr], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)

            return true
        } catch (error) {
            console.error('[ExportLocalReport] Error exporting local report data:', error)
            alert('Ocurrió un error al generar el respaldo local: ' + error.message)
            return false
        }
    }, [executionReport, report])

    const provider = {
        exportLocalReportData,
        executionReport,
        setExecutionReport,
        avanceHoja,
        avanceTotal,
        setAvanceHoja,
        setAvanceTotal,
        loading,
        setLoading,
        setLoadingMessage,
        messageOnReport,
        actualizacionEjecucion,
        ejecucionActualizada,
        setMessageBacklog,
        messageBacklog,
        selectionItem,
        habilite3D,
        puedeEnviar,
        puedeCerrar,
        puedeRechazar,
        puedeTerminarJornada,
        materialesPreview,
        totalBacklogs,
        mensajeTerminoJornada,
        forwardReport,
        rejectReport,
        vistaPrevia,
        openMessages,
        closeMessages,
        openMessagesModal,
        openBacklogsResume,
        contenidoResumenBacklog,
        openMenu,
        setOpenMenu,
        setIsTermJornada,
        setOpenReportCommitModal,
        toForward,
        ejecucion,
        setEjecucion,
        saveExecutionReportToDb,
        openPreviewModal,
        setOpenPreviewModal,
        contentData,
        setContentData,
        saveItem,
        indexGroup,
        setIndexGroup,
        getExecutionReport,
        startReport,
        backlogImages,
        loadingImages,
        uploadProgress,
        itemsPendingSync,
        isSyncing,
        isDownloadingPautas,
        downloadPautasProgress,
        downloadPautasStatusText,
        bulkSyncExecutionReports,
        cancelDownloadPautas,
        bulkAssignReports,
        reportSyncStatus,
        syncQueue,
        globalSyncStatus,
        ...(process.env.NODE_ENV === 'test' && {
            __test__: {
                setSyncQueue,
                setProcessedCounts,
                setProcessedCount,
                setRetryTrigger,
                syncRetryTimeoutRef,
                blobUrlsRef
            }
        })
    }

    return (
        <>
            <ExecutionReportContext.Provider value={provider} {...props} />
            {openReportCommitModal && (
                <ReportCommitDialog
                    isTermJornada={isTermJornada}
                    terminarjornada={continuarTerminarJornada}
                    puedeEnviar={puedeEnviar}
                    open={openReportCommitModal}
                    closeModal={handleCommitModalResult}
                    report={report}
                    messageType={messageType}
                />
            )}
            {openMessagesModal && (
                <ReportMessagesDialog
                    open={openMessagesModal}
                    close={closeMessages}
                    report={report}
                />
            )}
            {openResumeBacklogsDialog && (
                <BacklogsResume
                    setOpenResumeBacklogsDialog={setOpenResumeBacklogsDialog}
                    openResumeBacklogsDialog={openResumeBacklogsDialog}
                    contenidoResumenBacklog={contenidoResumenBacklog}
                />
            )}
        </>
    )
}

export const useExecutionReportContext = () =>
    useContext(ExecutionReportContext)

export { cleanAzureUrl, injectBase64IntoExecutionReport }
